/**
 * Shared safety helpers for every WhatsApp outbound-send surface.
 *
 * Dependency-free on purpose: imported by all three send surfaces
 * (whatsappV2 thread page, whatsappAccount lead-detail compose, the V1
 * whatsapp slice) and unit tested on its own, without dragging the
 * store/axios graph into Jest.
 *
 * The distinction that matters is NOT "did it succeed?" but "do we KNOW what
 * happened?". `WhatsappPhone._send_message` calls Meta before the request
 * finishes (it has to, to get the wamid back), so anything that goes wrong
 * after that point leaves the message already with the patient while the
 * operator is looking at an error. Sentry JAVASCRIPT-REACT-13E is exactly that
 * case: a 500 raised in audit middleware AFTER a successful send, shown to the
 * operator as "Failed to send WhatsApp message. Please try again." — an
 * invitation to message the patient twice.
 */

// Axios codes for "the request left the browser but no usable reply came back".
// The default apiCall timeout is 10s, which a slow Meta round-trip can exceed,
// so the backend may still be completing the send.
export const AMBIGUOUS_TRANSPORT_CODES = new Set(['ECONNABORTED', 'ETIMEDOUT']);

/**
 * The one wording used whenever a send's outcome is unknown. Kept here so all
 * three send surfaces say the same thing and so a copy change is one edit.
 */
export const UNCONFIRMED_SEND_MESSAGE = "We couldn't confirm this message was sent. "
    + 'Check the conversation before sending it again.';

/**
 * True when the send's outcome is UNKNOWN and a blind resend would risk
 * double-messaging the patient.
 *
 *  - `status >= 500`: the server answered, but the Meta call may already have
 *    run before it failed.
 *  - `status === 0` with an aborted/timed-out request: the POST left the
 *    browser and the backend may still be finishing it.
 *
 * A true network error (`ERR_NETWORK` — nothing left the browser) is NOT
 * ambiguous: the send definitely did not happen, so it keeps the ordinary
 * "not sent, try again" handling.
 *
 * @param {{status?: number, error?: {code?: string}}} response structured
 *   result from `callApi`.
 * @returns {boolean}
 */
export const isUnconfirmedSendOutcome = (response) => {
    const status = response?.status;
    if (typeof status === 'number' && status >= 500) return true;
    return status === 0 && AMBIGUOUS_TRANSPORT_CODES.has(response?.error?.code);
};

/**
 * Should we re-POST with the same `client_message_id` to find out what happened?
 *
 * ONLY for a real 5xx RESPONSE. A timeout deliberately does NOT qualify: the
 * first request may still be running server-side, where the idempotency slot
 * still reads `PENDING` — the backend would fall through and really send a
 * SECOND message. That is the whole distinction between this predicate and
 * `isUnconfirmedSendOutcome`, which is the wider "do not offer a retry" set.
 *
 * @param {{status?: number}} response structured result from `callApi`.
 * @returns {boolean}
 */
export const shouldRecheckSend = (response) => {
    const status = response?.status;
    return typeof status === 'number' && status >= 500;
};

export default isUnconfirmedSendOutcome;

/**
 * One idempotency key per logical Send click, sent as `client_message_id`.
 *
 * The backend caches it per phone for 60s and REPLAYS the original
 * WhatsappMessage on a duplicate POST instead of calling Meta again
 * (`WhatsappPhoneViewSet.send_message`). That is what makes a retry after an
 * unconfirmed outcome safe — without a key, a retry is a second real message.
 *
 * Generate it ONCE per click and reuse the same value for every attempt of that
 * click; a fresh key per attempt defeats the guard entirely.
 *
 * @returns {string}
 */
export const newClientMessageId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `cmid-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};
